// @ts-nocheck

import { useEffect, useCallback } from 'react';

import { pick, omit } from 'lodash';

import type { Game } from '@shared/types';

import useAxios from '@services/api';

import { GameBuild } from './client.types';
import type { SubmittedForm, CropPixelsData } from './client.types';
import { cropImage, rotateImages, resizeImage } from './client.utils';

const fixedPixels = { x: 100, y: 100, width: 200, height: 200 };

export const useFetchGameDefaults = (name: string) => {
  const { response, error, issueRequest } = useAxios<Game>();

  useEffect(() => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, [name]);

  return response;
};

export const useFetchGameDefaultsLazy = () => {
  const { response, error, issueRequest, loading } = useAxios<Game>();

  const useFetchGameDefaultsLazy = useCallback((name: string) => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, []);

  return [response, useFetchGameDefaultsLazy, loading] as const;
};

export const useFetchGameBuild = () => {
  const { response, error, issueRequest } = useAxios();

  const useFetchGameBuild = useCallback((gameId: string) => {
    void issueRequest({ url: `/game/build/${gameId}`, method: 'get' });
  }, []);
  return [response, useFetchGameBuild, error] as const;
};

// export const useFetchGameBuild = (id: string) => {
//   const { response, error, issueRequest } = useAxios<GameBuild>();

//   useEffect(() => {
//     if (!id) return;  // Проверка на пустой ID

//     void issueRequest({ 
//       url: `/game/build/${id}`, 
//       method: 'get', 
//       headers: {
//         'x-api-key': '38f8ef5e-8c83-416d-9b6d-bd9980c44429',
//       },
//     });
//   }, [id]);

//   return [response, error] as const;
// };


export const useCreateGameBuild = () => {
  const { response, error, issueRequest, loading, setLoading } =
    useAxios<GameBuild>();

  const handleSubmit = useCallback(
    (
      data: SubmittedForm,
      croppedPixelsData: CropPixelsData,
      tiles: string[]
    ) => {
      setLoading(true);
      console.log('Начало загрузки файлов:', { 
        startGameImage: data.startGameImage,
        endGameImage: data.endGameImage,
        gameTiles: data.gameTiles,
        customMusicFile: data.customMusicFile
      });

      const pendingPromises = [];

      for (const [fieldName, pixels] of Object.entries(croppedPixelsData)) {
        if (!pixels) {
          continue;
        }
        if (!isNaN(parseInt(fieldName, 10))) {
          pendingPromises.push(
            cropImage(data.gameTiles[fieldName], pixels).then(
              (croppedImage) => {
                data.gameTiles[fieldName] = croppedImage;
              }
            )
          );
        } else {
          pendingPromises.push(
            cropImage(data[fieldName], pixels).then((croppedImage) => {
              data[fieldName] = croppedImage;
            })
          );
        }
      }

      Promise.all(pendingPromises)
        .then(() => {
          console.log('Все изображения обрезаны, начинаем подготовку к отправке');
          
          data.gameTiles = rotateImages(tiles);

          const formData = new FormData();

          const fileFields = [
            'startGameImage',
            'endGameImage',
            'gameTiles',
            'customMusicFile',
          ];
          const fileUrls = <(string | null)[]>[];

          if (!data.enableCustomMusic) {
            delete data.customMusicFile;
          }

          const files: Pick<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'gameTiles' | 'customMusicFile'
          > = pick(data, fileFields);
          const plainFields: Omit<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'customMusicFile' | 'gameTiles'
          > & { gameTiles: (string | null)[] } = omit(data, fileFields);

          console.log('Подготовка файлов для отправки:', { 
            files: Object.keys(files),
            plainFields: Object.keys(plainFields)
          });

          for (const [fieldName, fileObject] of Object.entries(files)) {
            if (fieldName === 'gameTiles') {
              for (let i = 0; i < files[fieldName].length; i++) {
                const imageFile = files[fieldName][i];
                if (typeof imageFile === 'string') {
                  fileUrls.push(imageFile);
                } else if (imageFile instanceof File) {
                  fileUrls.push(null);
                  formData.append(`gameTiles[${i}]`, imageFile);
                }
              }
            } else if (fileObject instanceof File) {
              formData.append(fieldName, fileObject);
            } else if (typeof fileObject === 'string') {
              if (fieldName === 'customMusicFile') {
                plainFields.musicUrl = fileObject;
              } else {
                fileUrls.push(fileObject);
              }
            }
          }

          plainFields.gameTiles = fileUrls;

          const jsonData = JSON.stringify(plainFields);

          formData.append('jsonData', jsonData);

          console.log('Отправка данных на сервер...');
          void issueRequest({
            url: `/games/build/memory`,
            method: 'put',
            body: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        })
        .finally(() => {
          console.log('Загрузка завершена');
          setLoading(false);
        });
    },
    []
  );

  return [response, handleSubmit, loading] as const;
};

export const useSetActivateGameDate = () => {
  const { response, error, issueRequest } = useAxios<Record<string, never>>();

  const useSetActivateGameDate = useCallback((id: string) => {
    void issueRequest({ url: `/games/keys/${id}`, method: 'put' });
  }, []);

  return [response, useSetActivateGameDate] as const;
};
